import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, Search, Send, Loader2, Bot, User, 
  CheckCircle, AlertCircle, Mail, MessageSquare
} from "lucide-react";

const ContactPage = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState(1); // 1: Self-service, 2: AI Chat, 3: Contact Form
  const [searchQuery, setSearchQuery] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [ticketSubmitted, setTicketSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const chatEndRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Show contact form after 3 failed AI attempts
  useEffect(() => {
    if (attemptCount >= 3 && !showContactForm) {
      setShowContactForm(true);
    }
  }, [attemptCount, showContactForm]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setStage(2);
      handleChatMessage(searchQuery);
      setSearchQuery("");
    }
  };

  const handleChatMessage = async (message) => {
    setChatMessages(prev => [...prev, { role: "user", content: message }]);
    setChatLoading(true);
    setAttemptCount(prev => prev + 1);

    try {
      const response = await axios.post(`${API}/support/chat`, {
        message: message,
        session_id: sessionId,
        attempt_count: attemptCount + 1
      });

      setSessionId(response.data.session_id);
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: response.data.response,
        resolved: response.data.resolved
      }]);

      // If not resolved after 2 attempts, hint about human support
      if (!response.data.resolved && attemptCount >= 1) {
        setTimeout(() => {
          setChatMessages(prev => [...prev, {
            role: "system",
            content: attemptCount >= 2 
              ? "It seems I'm having trouble fully resolving your question. A human support option is now available below."
              : "If I'm not able to help, you can always reach our human support team."
          }]);
        }, 1000);
      }
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm having connection issues. Please try again or use the contact form below.",
        resolved: false
      }]);
      setShowContactForm(true);
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;
    handleChatMessage(chatInput.trim());
    setChatInput("");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const response = await axios.post(`${API}/support/contact`, {
        ...formData,
        chat_history: chatMessages.map(m => ({ role: m.role, content: m.content }))
      });

      setTicketId(response.data.ticket_id);
      setTicketSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  if (ticketSubmitted) {
    return (
      <div className="min-h-screen py-8 px-6 flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-primary mb-2">Message Received!</h1>
          <p className="text-muted-foreground mb-4">
            Your ticket ID is: <span className="font-mono font-bold">{ticketId?.slice(0, 8)}</span>
          </p>
          <p className="text-muted-foreground mb-6">
            We'll respond to your inquiry within 24-48 hours at the email address you provided.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate("/")} variant="outline">
              Return Home
            </Button>
            <Button onClick={() => navigate("/faq")} className="bg-primary">
              Browse FAQ
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/faq")}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to FAQ
          </button>
          <h1 className="text-3xl sm:text-4xl font-bold text-primary mb-2" data-testid="contact-title">
            Contact Support
          </h1>
          <p className="text-muted-foreground">
            We're here to help! Let's find the answer together.
          </p>
        </div>

        {/* Stage Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`flex items-center gap-2 ${s <= stage ? "text-primary" : "text-muted-foreground"}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < stage ? "bg-primary text-white" : 
                s === stage ? "bg-primary/20 text-primary border-2 border-primary" : 
                "bg-muted"
              }`}>
                {s < stage ? <CheckCircle className="h-4 w-4" /> : s}
              </div>
              <span className="text-sm hidden sm:inline">
                {s === 1 ? "Search" : s === 2 ? "AI Help" : "Human Support"}
              </span>
              {s < 3 && <div className="w-8 h-0.5 bg-muted" />}
            </div>
          ))}
        </div>

        {/* Stage 1: Self-Service Search */}
        {stage === 1 && (
          <div className="bg-card rounded-2xl border border-primary/10 p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-primary mb-2">How can we help you today?</h2>
              <p className="text-muted-foreground">
                Start by describing your question or issue
              </p>
            </div>

            <form onSubmit={handleSearchSubmit}>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="e.g., How do I download my report?"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 text-lg pr-24"
                  data-testid="search-input"
                />
                <Button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-primary"
                  disabled={!searchQuery.trim()}
                  data-testid="search-submit"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </div>
            </form>

            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              <span className="text-sm text-muted-foreground">Popular:</span>
              {["Report download", "Refund policy", "Visa questions", "Payment issues"].map((topic) => (
                <button
                  key={topic}
                  onClick={() => {
                    setSearchQuery(topic);
                    setStage(2);
                    handleChatMessage(topic);
                  }}
                  className="text-sm px-3 py-1 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Stage 2: AI Troubleshooting */}
        {stage >= 2 && (
          <div className="bg-card rounded-2xl border border-primary/10 overflow-hidden mb-6">
            <div className="bg-primary/5 px-6 py-4 border-b border-primary/10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-primary">AI Support Assistant</h3>
                <p className="text-sm text-muted-foreground">I'll do my best to help you</p>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4">
              {chatMessages.map((msg, i) => (
                <div
                  key={"chat-contact-" + i}
                  className={`flex gap-3 ${
                    msg.role === "user" ? "justify-end" : 
                    msg.role === "system" ? "justify-center" : "justify-start"
                  }`}
                >
                  {msg.role === "system" ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-4 py-2 rounded-full">
                      <AlertCircle className="h-4 w-4" />
                      {msg.content}
                    </div>
                  ) : (
                    <>
                      {msg.role === "assistant" && (
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Bot className="h-4 w-4 text-primary" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] rounded-xl px-4 py-3 ${
                          msg.role === "user"
                            ? "bg-primary text-white"
                            : "bg-muted"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      {msg.role === "user" && (
                        <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-secondary" />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {chatLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Loader2 className="h-4 w-4 text-primary animate-spin" />
                  </div>
                  <div className="bg-muted rounded-xl px-4 py-3">
                    <p className="text-sm text-muted-foreground">Thinking...</p>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <form onSubmit={handleChatSubmit} className="border-t border-primary/10 p-4 flex gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type your follow-up question..."
                className="flex-1"
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                disabled={chatLoading || !chatInput.trim()}
                className="bg-primary"
                data-testid="chat-send"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        )}

        {/* Stage 3: Human Support Form (Dynamic Visibility) */}
        {showContactForm && stage >= 2 && (
          <div className="bg-card rounded-2xl border border-secondary/20 overflow-hidden">
            <div className="bg-secondary/5 px-6 py-4 border-b border-secondary/10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-secondary" />
              </div>
              <div>
                <h3 className="font-semibold text-primary">Contact Our Team</h3>
                <p className="text-sm text-muted-foreground">We'll respond within 24-48 hours</p>
              </div>
            </div>

            <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    required
                    data-testid="form-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    required
                    data-testid="form-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  required
                  data-testid="form-subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Please describe your issue in detail..."
                  rows={4}
                  required
                  data-testid="form-message"
                />
              </div>

              <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                <MessageSquare className="h-4 w-4 inline mr-2" />
                Your chat history will be included to help our team understand your issue better.
              </div>

              <Button
                type="submit"
                disabled={formLoading}
                className="w-full btn-animated bg-secondary hover:bg-secondary/90"
                data-testid="form-submit"
              >
                {formLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Manual trigger for human support (if not auto-shown) */}
        {stage >= 2 && !showContactForm && (
          <div className="text-center mt-4">
            <button
              onClick={() => {
                setShowContactForm(true);
                setStage(3);
              }}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="show-form-btn"
            >
              Need to speak with a human? Click here
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactPage;
